import React from "react";
import MaterialTable, { MTableToolbar } from "material-table";
import { makeStyles } from "@material-ui/core/styles";
import { Chip } from "@material-ui/core";
import { parseafechainicioServicio } from "functions/formatoFechas";
import PropTypes from "prop-types";

const useStyles = makeStyles({
  totalsBar: {
    display: "flex",
    gap: 16,
    padding: "8px 16px",
    flexWrap: "wrap"
  },
  totalCard: {
    background: "#f5f5f5",
    borderRadius: 6,
    padding: "4px 12px",
    fontSize: 13,
    fontWeight: 500,
    color: "#333"
  },
  totalCardPending: {
    background: "#fff3e0",
    borderRadius: 6,
    padding: "4px 12px",
    fontSize: 13,
    fontWeight: 500,
    color: "#e65100"
  },
  totalCardPaid: {
    background: "#e8f5e9",
    borderRadius: 6,
    padding: "4px 12px",
    fontSize: 13,
    fontWeight: 500,
    color: "#2e7d32"
  }
});

const ESTATUS_LABELS = { A: "Pagado", P: "Pendiente", M: "Moratorio" };
const ESTATUS_COLORS = { A: "default", P: "primary", M: "secondary" };

const calcMontoReal = rowData => {
  if (rowData.estatus === "A") return null;
  const monto = parseFloat(rowData.montoapagar) || 0;
  const interes = parseFloat(rowData.interes) || 0;
  const anticipado = parseFloat(rowData.anticipado) || 0;
  return (monto + interes - anticipado).toFixed(2);
};

const PaymentsTable = ({ data, editableDates = false, onRowSelect }) => {
  const classes = useStyles();

  // Primera parcialidad pendiente — la única seleccionable
  const primeraPendiente = data ? data.find(i => i.estatus !== "A") : null;
  const idPrimeraPendiente = primeraPendiente ? primeraPendiente.id : null;

  const fmtMoney = val => "$" + parseFloat(val || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 });

  const columns = [
    {
      title: "No.", field: "id", type: "numeric", editable: "never", width: "5%",
      headerStyle: { textAlign: "center" }, cellStyle: { textAlign: "center" }
    },
    {
      title: "Estatus", field: "estatus", editable: "never", width: "10%",
      headerStyle: { textAlign: "center" }, cellStyle: { textAlign: "center" },
      render: rowData => {
        const ant = parseFloat(rowData.anticipado) || 0;
        const label = rowData.estatus === "P" && ant > 0 ? "Abono parcial" : (ESTATUS_LABELS[rowData.estatus] || rowData.estatus);
        const chipStyle = rowData.estatus === "P" && ant > 0
          ? { backgroundColor: "#ff9800", color: "#fff" }
          : rowData.estatus === "M" ? { backgroundColor: "#e53935", color: "#fff" } : {};
        return (
          <Chip
            label={label}
            color={Object.keys(chipStyle).length ? "default" : (ESTATUS_COLORS[rowData.estatus] || "default")}
            size="small"
            style={chipStyle}
          />
        );
      }
    },
    {
      title: "Fecha a pagar", field: "fechaapagar",
      editable: editableDates ? "always" : "never", width: "13%",
      headerStyle: { textAlign: "center" }, cellStyle: { textAlign: "center" }
    },
    {
      title: "Fecha de pago", field: "fechapago",
      editable: editableDates ? "always" : "never", width: "13%",
      headerStyle: { textAlign: "center" }, cellStyle: { textAlign: "center" },
      render: rowData => rowData.fechapago || "—"
    },
    {
      title: "Monto original", field: "montoapagar", editable: "never", width: "15%",
      headerStyle: { textAlign: "right" }, cellStyle: { textAlign: "right" },
      render: rowData => fmtMoney(rowData.montoapagar)
    },
    {
      title: "Abonado", field: "anticipado", editable: "never", width: "12%",
      headerStyle: { textAlign: "right" }, cellStyle: { textAlign: "right" },
      render: rowData => {
        const ant = parseFloat(rowData.anticipado) || 0;
        if (rowData.estatus === "A" || ant === 0) return "—";
        return <span style={{ color: "#2e7d32", fontWeight: 500 }}>{fmtMoney(ant)}</span>;
      }
    },
    {
      title: "Por pagar", field: "montoRealAPagar", editable: "never", width: "15%",
      headerStyle: { textAlign: "right" }, cellStyle: { textAlign: "right", fontWeight: 600 },
      render: rowData => {
        const val = calcMontoReal(rowData);
        if (val === null) return <span style={{ color: "#4caf50" }}>Pagado</span>;
        return <span style={{ color: "#1565c0" }}>{fmtMoney(val)}</span>;
      }
    }
  ];

  if (!data) return null;

  const totalPending = data
    .filter(i => i.estatus !== "A")
    .reduce((sum, i) => { const val = calcMontoReal(i); return sum + (val ? parseFloat(val) : 0); }, 0);

  const totalPaid = data
    .filter(i => i.estatus === "A")
    .reduce((sum, i) => sum + (parseFloat(i.montoapagar) || 0), 0);

  const totalContrato = totalPaid + totalPending;

  return (
    <MaterialTable
      title="Tabla de pagos"
      columns={columns}
      data={data}
      onRowClick={(_, rowData) => {
        // Solo permitir seleccionar la primera parcialidad pendiente
        if (rowData.id === idPrimeraPendiente && rowData.estatus !== "A" && typeof onRowSelect === "function") {
          onRowSelect(rowData);
        }
      }}
      options={{
        search: true, paging: true, pageSize: 10,
        headerStyle: { fontWeight: "bold", backgroundColor: "#fafafa" },
        rowStyle: rowData => {
          if (rowData.estatus === "A") return { backgroundColor: "#f5f5f5", color: "#9e9e9e", cursor: "default" };
          if (rowData.id === idPrimeraPendiente) {
            if (rowData.estatus === "M") return { backgroundColor: "#ffebee", cursor: "pointer" };
            return { backgroundColor: "#e3f2fd", cursor: "pointer", fontWeight: 500 };
          }
          // Parcialidades pendientes pero no seleccionables (no es la primera)
          if (rowData.estatus === "M") return { backgroundColor: "#ffebee", opacity: 0.5, cursor: "default" };
          return { opacity: 0.5, cursor: "default" };
        }
      }}
      components={{
        Toolbar: props => (
          <div>
            <MTableToolbar {...props} />
            <div className={classes.totalsBar}>
              <div className={classes.totalCard}>
                Total contrato: <strong>{fmtMoney(totalContrato)}</strong>
              </div>
              <div className={classes.totalCardPending}>
                Pendiente: <strong>{fmtMoney(totalPending)}</strong>
              </div>
              <div className={classes.totalCardPaid}>
                Pagado: <strong>{fmtMoney(totalPaid)}</strong>
              </div>
            </div>
          </div>
        )
      }}
      editable={editableDates ? { onRowUpdate: () => new Promise(resolve => resolve()) } : {}}
      localization={{ body: { emptyDataSourceMessage: "Sin parcialidades" } }}
    />
  );
};

PaymentsTable.propTypes = {
  data: PropTypes.array,
  editableDates: PropTypes.bool,
  onRowSelect: PropTypes.func
};

export default PaymentsTable;
